import { useMemo, useState } from 'react';
import { Loader2, Plus, Search } from 'lucide-react';
import { toast } from 'sonner';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { useDebouncedValue } from '@/hooks/use-debounce';
import { useSearchTasks, useCreateTask } from '@/hooks/use-tasks';
import { useAddTasksToSprint } from '@/hooks/use-sprints';
import { extractErrorMessage } from '@/lib/errors';
import { useBoardPermission } from '@/hooks/use-boards';

interface SprintIssueAddProps {
  boardId: string;
  sprintId: string;
  existingIssueIds: string[];
}

export function SprintIssueAdd({ boardId, sprintId, existingIssueIds }: SprintIssueAddProps) {
  const [query, setQuery] = useState('');
  const [focused, setFocused] = useState(false);
  const debouncedQuery = useDebouncedValue(query, 250);

  const canSearch = debouncedQuery.trim().length >= 2;
  const { data: results, isFetching } = useSearchTasks(
    boardId,
    canSearch ? debouncedQuery : '',
  );

  const addTasks = useAddTasksToSprint(boardId);
  // Putting an issue into a sprint is task management on this board.
  const canAdd = useBoardPermission(boardId, 'task.manage');
  const createTask = useCreateTask(boardId);

  const existing = useMemo(() => new Set(existingIssueIds), [existingIssueIds]);
  const matches = useMemo(
    () => (results ?? []).filter((t) => !existing.has(t.id)),
    [results, existing],
  );

  const trimmed = query.trim();
  const showDropdown = focused && trimmed.length >= 2;
  const showCreateHint = showDropdown && !isFetching && matches.length === 0 && trimmed.length > 0;

  const clear = () => {
    setQuery('');
  };

  const selectMatch = (taskId: string) => {
    addTasks.mutate(
      { sprintId, taskIds: [taskId] },
      {
        onSuccess: () => toast('Issue moved to this sprint'),
        onError: (e) => toast.error(extractErrorMessage(e, 'Failed to add issue')),
      },
    );
    clear();
  };

  const createIssue = async () => {
    if (!trimmed) return;
    try {
      await createTask.mutateAsync({ title: trimmed, sprintId });
      toast.success('Issue created');
      clear();
    } catch (e) {
      toast.error(extractErrorMessage(e, 'Failed to create issue'));
    }
  };

  // This whole control is one write. A board that does not allow it should not
  // show a search box that ends in a refusal.
  if (!canAdd) return null;

  return (
    <div className="relative">
      <div className="flex items-center gap-2 rounded-md border border-border bg-background px-2.5 py-1.5">
        <Search className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => setTimeout(() => setFocused(false), 120)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              e.preventDefault();
              clear();
              e.currentTarget.blur();
            }
            if (e.key === 'Enter') {
              e.preventDefault();
              if (!isFetching && matches.length === 0 && trimmed.length > 0) {
                void createIssue();
              }
            }
          }}
          placeholder="Search or create an issue…"
          className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
        />
        {(isFetching || createTask.isPending) && (
          <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin text-muted-foreground" />
        )}
      </div>

      {showDropdown && (matches.length > 0 || showCreateHint) && (
        <div className="absolute z-20 mt-1 w-full overflow-hidden rounded-md border border-border bg-popover shadow-md">
          <div className="max-h-[220px] overflow-y-auto">
            {matches.map((t) => (
              <button
                key={t.id}
                type="button"
                // onMouseDown fires before the input's blur, so the click isn't swallowed.
                onMouseDown={(e) => {
                  e.preventDefault();
                  selectMatch(t.id);
                }}
                className="flex w-full items-center gap-2.5 px-3 py-2 text-left transition-colors hover:bg-accent cursor-pointer"
              >
                <span
                  className="h-2 w-2 shrink-0 rounded-full"
                  style={{ backgroundColor: t.status.color }}
                />
                <span className="shrink-0 font-mono text-xs text-muted-foreground">{t.key}</span>
                <span className="flex-1 truncate text-sm">{t.title}</span>
                {t.assignee && (
                  <Avatar className="h-4 w-4 shrink-0">
                    <AvatarFallback className="text-[7px]">
                      {t.assignee.firstName[0]}
                      {t.assignee.lastName[0]}
                    </AvatarFallback>
                  </Avatar>
                )}
              </button>
            ))}

            {showCreateHint && (
              <button
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault();
                  void createIssue();
                }}
                className="flex w-full items-center gap-2.5 px-3 py-2 text-left transition-colors hover:bg-accent cursor-pointer"
              >
                <Plus className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                <span className="text-sm">
                  Create issue “<span className="font-medium">{trimmed}</span>”
                </span>
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
